import { Button, Dropdown, Flex, Spin, Typography } from 'antd';
import { useState } from 'react';
import { generatePath, Link, useParams } from 'react-router-dom';

import IconArrowRight from '@/assets/icons/arrow-right.svg?react';
import IconDown from '@/assets/icons/down.svg?react';
import { ROUTES_PATH } from '@/constants/routes.constants';
import { DEFAULT_DROPDOWN_OPTIONS } from '@/constants/table.constants';
import { ShopTable } from '@/features/shop/components/tables/shop-table';
import { useDeleteShop } from '@/features/shop/mutations';
import type { ShopContractTypeEnum } from '@/features/shop/types';
import { useUserInformation } from '@/hooks/use-user-information';

import { useOrganizationShops } from '../queries/shops';

const { Text } = Typography;

function getRoutePath(id: string | undefined, isWeddingChain = false): string {
  if (id) {
    return generatePath(
      isWeddingChain
        ? ROUTES_PATH.organizationShopsWeddingsNew
        : ROUTES_PATH.organizationShopsNew,
      { id },
    );
  }

  return isWeddingChain
    ? ROUTES_PATH.myOrganizationShopsWeddingsNew
    : ROUTES_PATH.myOrganizationShopsNew;
}

interface OrganizationShopsProps {
  activeKey: string;
  isWeddingChain?: boolean;
  contractType?: ShopContractTypeEnum;
}

export function OrganizationShops({
  activeKey,
  contractType,
  isWeddingChain = false,
}: Readonly<OrganizationShopsProps>) {
  const { id } = useParams();
  const [currentPage, setCurrentPage] = useState(1);
  const { organizationId, isSbsSaleOrSbsTagTeamRole } = useUserInformation();
  const currentOrganizationId = id ?? organizationId!;

  const { data, isLoading, isFetching, refetch } = useOrganizationShops(
    currentOrganizationId,
    { page: currentPage },
    { enabled: activeKey === 'shops' },
  );
  const {
    shops,
    total_records: totalRecords,
    is_store_registration_approved: isStoreRegistrationApproved,
  } = data ?? {};
  const isDisableCreateShop = !isStoreRegistrationApproved && !!shops?.length;

  const { mutate: deleteShop } = useDeleteShop({ onSuccess: () => refetch() });

  const items = [
    {
      key: '0',
      label: (
        <Link to={getRoutePath(id)}>
          <Flex align="center" justify="space-between">
            <Text>新店開業申請</Text>
            <IconArrowRight className="ml-36" />
          </Flex>
        </Link>
      ),
    },
    {
      key: '1',
      disabled: !isStoreRegistrationApproved,
      label: (
        <Link to={getRoutePath(id, true)}>
          <Flex align="center" justify="space-between">
            <Text type={isStoreRegistrationApproved ? undefined : 'secondary'}>
              企画作成
            </Text>
            <IconArrowRight />
          </Flex>
        </Link>
      ),
    },
  ];

  if (isLoading) return <Spin size="small" />;

  return (
    <>
      <Flex justify="space-between" align="center" className="mb-16">
        <Text className="text-md" type="secondary">
          全{totalRecords}件
        </Text>
        {!isSbsSaleOrSbsTagTeamRole &&
          (isWeddingChain ? (
            <Dropdown
              menu={{ items: items }}
              {...DEFAULT_DROPDOWN_OPTIONS}
              align={{ offset: [0, 8] }}
              placement="bottomRight"
            >
              <Button
                type="primary"
                iconPosition="end"
                icon={<IconDown className="ml-12" />}
                disabled={isDisableCreateShop}
              >
                新規作成
              </Button>
            </Dropdown>
          ) : (
            <Link to={getRoutePath(id)}>
              <Button type="primary" disabled={isDisableCreateShop}>
                新店開業申請
              </Button>
            </Link>
          ))}
      </Flex>
      <ShopTable
        shopNameTitle={isWeddingChain ? '店舗・企画' : '店舗'}
        dataSource={shops}
        loading={isFetching}
        contractType={contractType}
        isWeddingChain={isWeddingChain}
        deleteShop={deleteShop}
        pagination={{
          total: totalRecords,
          onChange(page) {
            setCurrentPage(page);
          },
        }}
      />
    </>
  );
}
